import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

export type ThemeName =
  | 'ocean'
  | 'indigo'
  | 'violet'
  | 'rose'
  | 'ember'
  | 'amber'
  | 'emerald'
  | 'teal'
  | 'slate'
  | 'graphite';

export interface ThemeOption {
  id: ThemeName;
  label: string;
  color: string;
}

export const THEMES: ThemeOption[] = [
  { id: 'ocean',    label: 'Ocean',    color: '#1D4ED8' },
  { id: 'indigo',   label: 'Indigo',   color: '#4F46E5' },
  { id: 'violet',   label: 'Violet',   color: '#7C3AED' },
  { id: 'rose',     label: 'Rose',     color: '#E11D48' },
  { id: 'ember',    label: 'Ember',    color: '#EA580C' },
  { id: 'amber',    label: 'Amber',    color: '#D97706' },
  { id: 'emerald',  label: 'Emerald',  color: '#059669' },
  { id: 'teal',     label: 'Teal',     color: '#0D9488' },
  { id: 'slate',    label: 'Slate',    color: '#6366F1' },
  { id: 'graphite', label: 'Graphite', color: '#818CF8' },
];

interface ThemeContextValue {
  theme: ThemeName;
  savedTheme: ThemeName;
  setTheme: (t: ThemeName) => void;
  saveTheme: (t: ThemeName) => void;
  themes: ThemeOption[];
  isDark: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'ocean',
  savedTheme: 'ocean',
  setTheme: () => {},
  saveTheme: () => {},
  themes: THEMES,
  isDark: false,
  toggleDarkMode: () => {},
});

function userStorageKey(userId: string) {
  return `cotaskr_theme_${userId}`;
}
function lightThemeKey(userId: string) {
  return `cotaskr_light_theme_${userId}`;
}

const FALLBACK_KEY = 'cotaskr_theme_default';
const FALLBACK_LIGHT_KEY = 'cotaskr_light_theme_default';

export function applyTheme(name: ThemeName) {
  document.documentElement.setAttribute('data-theme', name);
  const isDark = name === 'graphite';
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<ThemeName>('ocean');
  const [savedTheme, setSavedTheme] = useState<ThemeName>('ocean');

  // Load saved theme whenever user changes (login/logout)
  useEffect(() => {
    const userSaved = user?.id ? localStorage.getItem(userStorageKey(user.id)) : null;
    const fallback = localStorage.getItem(FALLBACK_KEY);
    const raw = userSaved || fallback || 'ocean';
    const valid = (THEMES.find(t => t.id === raw)?.id ?? 'ocean') as ThemeName;
    setThemeState(valid);
    setSavedTheme(valid);
    applyTheme(valid);
  }, [user?.id]);

  /** Live preview — does NOT persist */
  const setTheme = (t: ThemeName) => {
    setThemeState(t);
    applyTheme(t);
  };

  /** Persist to localStorage keyed by user id */
  const saveTheme = (t: ThemeName) => {
    setTheme(t);
    setSavedTheme(t);
    if (user?.id) {
      localStorage.setItem(userStorageKey(user.id), t);
    }
    localStorage.setItem(FALLBACK_KEY, t);
  };

  /** Toggle between current light theme and Graphite (dark mode) */
  const toggleDarkMode = () => {
    if (theme === 'graphite') {
      const saved = user?.id ? localStorage.getItem(lightThemeKey(user.id)) : null;
      const fallback = localStorage.getItem(FALLBACK_LIGHT_KEY);
      const target = (saved || fallback || 'ocean') as ThemeName;
      saveTheme(THEMES.find(t => t.id === target) ? target : 'ocean');
    } else {
      if (user?.id) localStorage.setItem(lightThemeKey(user.id), theme);
      localStorage.setItem(FALLBACK_LIGHT_KEY, theme);
      saveTheme('graphite');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, savedTheme, setTheme, saveTheme, themes: THEMES, isDark: theme === 'graphite', toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
